import type {FC} from 'react';
import {useEffect, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {useQueryClient, useQuery, useMutation} from '@tanstack/react-query';
import moment from 'moment-timezone';
import {
  groupsUpdate,
  groupsEventsPartialUpdate,
  groupsEventsCreate,
} from 'api/v2';
import type {Group} from 'api/v2';
import type {UICalendarEventType} from '@wellstone-solutions/common';
import {Box, Stack} from '@wellstone-solutions/web';
import {BridgeEventSources, Hooks} from '@wellstone-solutions/common';
import {useStores} from 'hooks/useStores';
import {useEventSource} from 'hooks/useEventSource';
import {ALERT_TYPES, showAlert} from 'utils/showAlert';
import {ActionButtons, Form} from './Form';
import {
  groupSchema,
  groupEventSchema,
  MAX_NUM_GROUP_EVENTS,
} from '../constants';
import {LoadingIndicator} from 'components/shared/LoadingIndicator';
import {ErrorPage} from 'components/ErrorPage';
import {groupsQueryOptions, groupsEventsQueryOptions} from 'queries';
import {EventFormList} from './Form/EventFormList';
import {toV2Api, toV2EventUI} from '../utils';
import {PageHeader} from 'components/PageHeader';

const FORM_ID = 'new_group_form';

export const Edit: FC = () => {
  const [eventsUI, setEventsUI] = useState(
    Array(MAX_NUM_GROUP_EVENTS)
      .fill()
      .map((_, i) => ({isVisible: i === 0, isOpen: false})),
  );
  const {meStore, appUIStore} = useStores();
  const params = useParams();
  const groupId = params.group;

  const navigate = useNavigate();

  useEffect(() => {
    appUIStore.setBreadcrumbs([]);
  }, []);

  useEventSource(BridgeEventSources.GROUPS_PAGE);

  const {data, error, isLoading} = useQuery(groupsQueryOptions.detail(groupId));
  const groupEvents = useQuery(groupsEventsQueryOptions.list(groupId));

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({groupId, values}) => groupsUpdate(groupId, values),
  });

  const eventMutation = useMutation({
    mutationFn: ({groupId, eventId, patchedEvent}) =>
      groupsEventsPartialUpdate(groupId, eventId, patchedEvent),
    onSuccess: async () => {
      // Invalidate the group events query to refresh the list
      await queryClient.invalidateQueries({queryKey: ['group-events']});
      // We can safely navigate now that cache is updated
      navigate(`/groups/${groupId}`);
    },
  });

  const newEventMutation = useMutation({
    mutationFn: ({groupId, event}) => groupsEventsCreate(groupId, event),
    onSuccess: async () => {
      // Invalidate the group events query to refresh the list
      await queryClient.invalidateQueries({queryKey: ['group-events']});
      // We can safely navigate now that cache is updated
      navigate(`/groups/${groupId}`);
    },
  });

  useEffect(() => {
    // Reset the eventsUI state to match data
    if (groupEvents.data?.length > 0) {
      setEventsUI((prev) =>
        prev.map((_, index) => {
          const status = index < (groupEvents.data?.length ?? 0);
          return {
            isVisible: status, // Show all events that exist in the group
            isOpen: status, // Open all events that exist in the group
          };
        }),
      );
    }
  }, [groupEvents.isFetching]);

  // pre-allocate hooks to avoid re-rendering issues
  const groupEventForms: Array<UICalendarEventType> = [];
  for (let i = 0; i < MAX_NUM_GROUP_EVENTS; i++) {
    const {id: groupEventId, event} = groupEvents.data?.[i] ?? {};

    groupEventForms[i] = Hooks.useForm({
      enableReinitialize: true,
      initialValues: groupEventSchema.cast(
        groupEventId
          ? toV2EventUI({
              ...event,
              groupEventId,
              reminderTime: event?.invitations?.[0]?.reminderTime,
            })
          : {
              timezone: moment.tz.guess(),
              owner: meStore.me.id,
              createdBy: meStore.me.id,
            },
      ),
      schema: groupEventSchema,
      validationEngine: 'yup',
    });
  }

  const group: Group = groupSchema.cast({
    name: data?.name,
    description: data?.description,
    isPublic: data?.isPublic,
    organization: meStore.me.membership.organization.id,
    programs: data?.programs,
    category: data?.category,
  });

  const form = Hooks.useForm({
    enableReinitialize: true,
    initialValues: group,
    schema: groupSchema,
    validationEngine: 'yup',
    onSubmit: async (values) => {
      try {
        // group update
        if (form.dirty) {
          await mutation.mutateAsync({groupId, values});
          showAlert(`Group updated successfully!`, ALERT_TYPES.SUCCESS);
        }

        // Differentiate between new and edit group events
        // Update has initialValues and new do not
        const validEditGroupEventForms = groupEventForms.filter(
          (form) =>
            form.initialValues?.id?.length > 0 &&
            form.dirty &&
            form.isValid &&
            form.values.groupEventId,
        );

        const validNewGroupEventForms = groupEventForms.filter(
          (form) =>
            form.initialValues.id.length === 0 && form.dirty && form.isValid,
        );

        // ok, now send the updates
        if (validEditGroupEventForms.length > 0) {
          await Promise.all(
            validEditGroupEventForms.map((form) =>
              eventMutation.mutateAsync({
                groupId: groupId,
                eventId: form.values.groupEventId,
                patchedEvent: {
                  applyToAll: true,
                  originalStart: form.values.originalStart,
                  event: toV2Api(form.values),
                  reminderTime: form.values.reminderTime,
                },
              }),
            ),
          );

          showAlert(`Group event updated successfully!`, ALERT_TYPES.SUCCESS);
        }

        // and then add the new ones

        if (validNewGroupEventForms.length > 0) {
          await Promise.all(
            validNewGroupEventForms.map((form) =>
              newEventMutation.mutateAsync({
                groupId: groupId,
                event: {
                  event: toV2Api(form.values),
                  reminderTime: form.values.reminderTime,
                },
              }),
            ),
          );

          showAlert(`Group event updated successfully!`, ALERT_TYPES.SUCCESS);
        }
      } catch (error) {
        if (error.request?.responseURL?.search(/\/events(\/|$|\?|#)/) !== -1) {
          showAlert(`Unable to update scheduled event.`, ALERT_TYPES.ERROR);
          Object.entries(error.response?.data).forEach(([key, values]) => {
            console.error(`Error updating event: ${key}`, values);
            showAlert(`Error updating: ${key}`, ALERT_TYPES.ERROR);
          });
        } else {
          Object.entries(error?.data).forEach(([key, values]) => {
            form.setFieldError(key, values.join(' '));
          });
        }
      }
    },
  });

  if (error) {
    return <ErrorPage message="Group not found" isFullScreen={false} />;
  }

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <Box>
      <Stack direction={{xs: 'column', md: 'row'}}>
        <Stack
          flex={1}
          direction="column"
          justifyContent={{md: 'space-between'}}
          sx={{mb: 4}}>
          <PageHeader
            title="Edit Group Details"
            description="All fields are required unless indicated as optional."
          />

          <Form form={form} formId={FORM_ID} />

          <EventFormList
            groupId={groupId}
            forms={groupEventForms}
            eventsUI={eventsUI}
            setEventsUI={setEventsUI}
          />

          <ActionButtons
            formId={FORM_ID}
            form={form}
            groupEventForms={groupEventForms}
          />
        </Stack>
      </Stack>
    </Box>
  );
};
